import { AxiosResponse, ResponseType } from "axios";
import endpoints from "../utils/endpoints";
import {
  DownloadBlobConfigBehavior,
  downloadBlob,
  exportCsv,
  isBlob, exportXls,
} from "../utils/fileUtils";
import { isString } from "../utils/stringUtils";
import { Api } from "./api";
import { notificationError } from "./notification";
import { Output } from "./types";
import dayjs from "dayjs";
import {string} from "prop-types";

const responseTypes: Record<Output, ResponseType> = {
  [Output.HTML]: "text",
  [Output.PDF]: "blob",
  [Output.CSV]: "text",
  [Output.XLS]: "text",
};

export type DownloadOrRenderResponse =
  | {
      error: boolean;
      message: string;
      data: string;
    }
  | {
      errors: string[];
      status: string;
      report: null;
      uri: string;
    }
  | string
  | Blob;

export async function downloadOrRender<Params extends { output: Output }>(
  name: string,
  method: "get" | "post",
  url: string,
  params: Params
): Promise<string> {
  const DEFAULT_RESPONSE = "";

  try {
    let response: AxiosResponse<DownloadOrRenderResponse> | null = null;

    if (method === "get") {
      response = await Api.get<any, AxiosResponse<DownloadOrRenderResponse>>(
        url,
        {
          params,
          responseType: responseTypes[params.output],
        }
      );
    }

    if (method === "post") {
      response = await Api.post<any, AxiosResponse<DownloadOrRenderResponse>>(
        url,
        null,
        {
          params,
          responseType: responseTypes[params.output],
        }
      );
    }

    if (response === null) {
      return DEFAULT_RESPONSE;
    }

    if (
      typeof response.data === "object" &&
      "error" in response.data &&
      "message" in response.data
    ) {
      notificationError(`${response.data.message}`);
      return DEFAULT_RESPONSE;
    }

    if (
      typeof response.data === "object" &&
      "status" in response.data &&
      "errors" in response.data
    ) {
      let errorMessage = "Could not generate report"
      console.log(JSON.stringify(response.data.errors))
      if(Array.isArray(response.data.errors)) {
        errorMessage = response.data.errors[0] 
      }

      if(typeof response.data.errors === 'string') {
        errorMessage =  response.data.errors
      }

      notificationError(errorMessage);
      return DEFAULT_RESPONSE;
    }

    if (isBlob(response.data) && response.headers['content-type'].includes('application/json')) {
      response.data.text().then((text) => {
        const responseData = JSON.parse(text);
        let errorMessage = "Could not generate report"
        
        if(Array.isArray(responseData.errors)) {
          errorMessage = responseData.errors[0] 
        }

        if(typeof responseData.errors === 'string') {
          errorMessage =  responseData.errors
        }

        notificationError(errorMessage);
      }).catch((error) => {
        notificationError(`${error}`);
      });
      return DEFAULT_RESPONSE
    }

    if ((typeof response.data === "string" ) && response.headers['content-type'].includes('application/json')) {
      const responseData = JSON.parse(response.data);
      let errorMessage = "Could not generate report"

      if(Array.isArray(responseData.errors)) {
        errorMessage = responseData.errors[0]
      }

      if(typeof responseData.errors === 'string') {
        errorMessage =  responseData.errors
      }

      notificationError(errorMessage);
      return DEFAULT_RESPONSE
    }
    const today = dayjs();
    var filename = `${name} - ${today.format("MM-DD-YYYY")}`;

    if (name==='Monthly Reports' && !Output.isHTML(params.output)) {
      filename = response.headers['content-disposition']
          .split('=')[1];
      filename = filename.split('.')[0]
    }

    if (Output.isCSV(params.output) && isString(response.data)) {
      const today = dayjs();
      exportCsv(response.data,`${filename}`);
      return DEFAULT_RESPONSE;
    }

    if (Output.isXLS(params.output) && isString(response.data)) {
      const today = dayjs();
      exportXls(response.data,`${filename}`);
      return DEFAULT_RESPONSE;
    }

    if ((Output.isPDF(params.output) ) && isBlob(response.data)) {
      downloadBlob(response.data, {
        name: filename,
        behavior: DownloadBlobConfigBehavior.DIRECT_DOWNLOAD,
      });

      return DEFAULT_RESPONSE;
    }

    if (Output.isHTML(params.output) && isString(response.data)) {
      return response.data;
    }
    return DEFAULT_RESPONSE;
  } catch (error) {
    console.log(error)
    notificationError("Could not get report");
    return DEFAULT_RESPONSE;
  }
}

export type Report = {
  key: string;
  name: string;
  link: string;
  childs: Report[] | null;
};

export type GetReportsResult = Report[];

export type GetReportsResponse =
  | Report[]
  | {
      error: boolean;
      message: string;
    };

export async function getReports(): Promise<GetReportsResult> {
  try {
    const response = await Api.get<any, AxiosResponse<GetReportsResponse>>(
      endpoints.getMenu
    );

    if (!Array.isArray(response.data)) {
      notificationError(`${response.data.message}`);
      return [];
    }

    const menuItems = response.data;
    const reports = menuItems.find((menu) => menu.key === "reports");

    if (reports && Array.isArray(reports?.childs)) {
      return reports.childs;
    }

    return [];
  } catch (e) {
    notificationError("Could not get reports");
    return [];
  }
}

export async function getRealTimeTransactionLog(params: Record<string, any>) {
  let res = "";

  await Api.post("/reports-realtime/realtimetransactionlog?", null, {
    params,
  })
    .then((response) => {
      if (response.data.error) {
        notificationError(`${response.data.message}`);
      } else {
        res = response.data;
      }
    })
    .catch(() => {
      notificationError("Could not get report");
    });

  return res;
}

export type GetHistoricalInventoryParams = {
  name: string;
  dateIntervalStart: string;
  dateIntervalEnd: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  output: Output;
};

export function getHistoricalInventory(params: GetHistoricalInventoryParams) {
  return downloadOrRender<GetHistoricalInventoryParams>(
    "Historical Inventory Report",
    "post",
    endpoints.historicalinventory,
    params
  );
}

export type GetHistoricalTransactionLogParams = {
  dateIntervalStart: number;
  dateIntervalEnd: number;
  locationId?: string;
  output: Output;
  event?: string;
  device?: string;
  form?: string;
  serial?: string;
  limit?: string;
};

export function getHistoricalTransactionLog(
  params: GetHistoricalTransactionLogParams
) {
  return downloadOrRender<GetHistoricalTransactionLogParams>(
    "Historical Transaction Log Report",
    "post",
    endpoints.historicaltransactionlog,
    params
  );
}

export async function getReport(params: object &{name?:string}, url: string, output: Output) {
  return downloadOrRender<object & { output: Output }>(params.name || "Report", "get", url, {
    ...params,
    output,
  });
}

export async function getDashboardReport() {
  let res = {
    current_business_day: "",
    daily_game_summary: [],
    daily_jackpot_summary: {
      status: "",
      answer: [
        {
          id: "",
          template_id: "",
          instance_id: "",
          type: "",
          addition: 0,
          name: "",
          house_seed: 0,
          contribution_percent: "",
          tags: [],
          game_ids: [],
          partial_prize: 0,
          current_prize: 0,
          players_contribution: 0,
          start_timestamp: "",
          start_business_date: "",
        },
      ],
    },
    devices_daily_summary: {
      activated_devices: 0,
      active_session: 0,
    },
    location_count: 0,
  };
  await Api.get(endpoints.getDashboard)
    .then((response) => {
      if (response.data.error) {
        notificationError(`${response.data.message}`);
      } else {
        res = response.data;
      }
    })
    .catch((err: string) => {
      notificationError("Could not get report");
    });

  return res;
}

export async function getRealTimeLocationStatus(params: object) {
  let res = [
    {
      alias: "",
      ticket_prizes: 0,
      ticket_receipt_gross: 0,
      cash: 0,
      game_info: "",
      location_name: "",
      deviceType: "",
      date: new Date()
    },
  ];
  await Api.get(endpoints.getRealTimeLocationStatus, { params })
    .then((response) => {
      if (response.data.error) {
        notificationError(`${response.data.message}`);
      } else {
        res = response.data;
      }
    })
    .catch((err: string) => {
      notificationError("Could not get report");
    });

  return res;
}

export type GetClosedPullTabGamesParams = {
  name: string;
  dateIntervalStart: string;
  dateIntervalEnd: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  output: Output;
};

export function getClosedPullTabGames(params: GetClosedPullTabGamesParams) {
  return downloadOrRender<GetClosedPullTabGamesParams>(
    "Closed Pull-Tab Games",
    "get",
    endpoints.getClosedPullTabGames,
    params
  );
}

export type GetReportCurrentInventoryParams = {
  name: string;
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
};

export async function getReportCurrentInventory(
  params: GetReportCurrentInventoryParams
) {
  return downloadOrRender<GetReportCurrentInventoryParams>(
    "Current Inventory Report",
    "get",
    endpoints.currentinventory,
    params
  );
}

export type GetCurrentPulltabGamesParams = {
  name: string;
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
};

export function getCurrentPulltabGames(params: GetCurrentPulltabGamesParams) {
  return downloadOrRender<GetCurrentPulltabGamesParams>(
    "Current Pull-Tab Games",
    "get",
    endpoints.currentpulltabgames,
    params
  );
}

export type GetDeckStatusParams = {
  name:string;
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
};

export function getDeckStatus(params: GetDeckStatusParams) {
  return downloadOrRender<GetDeckStatusParams>(
    "Deck Status",
    "get",
    endpoints.deckStatus,
    params
  );
}

export type GetReportDistributorGameParams = {
  name: string;
  dateIntervalStart: string;
  dateIntervalEnd: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  output: Output;
};

export function getReportDistributorGame(
  params: GetReportDistributorGameParams
) {
  return downloadOrRender<GetReportDistributorGameParams>(
    "Distributor Game Report",
    "get",
    endpoints.distributorgame,
    params
  );
}

export type GetGameDefinitionParams = {
  form: string;
  output: Output;
};

export type GetGameDefinitionResponse =
  | {
      error: boolean;
      message: string;
      data: string;
    }
  | string
  | Blob;

export async function getGameDefinition(params: GetGameDefinitionParams) {
  const DEFAULT_RESPONSE = "";

  try {
    const response = await Api.get<
      any,
      AxiosResponse<GetGameDefinitionResponse>
    >(endpoints.gamedefinition, {
      params,
      responseType: responseTypes[params.output],
    });

    if (response === null) {
      return DEFAULT_RESPONSE;
    }

    if (
      typeof response.data === "object" &&
      "error" in response.data &&
      "message" in response.data
    ) {
      notificationError(`${response.data.message}`);
      return DEFAULT_RESPONSE;
    }

    if (Output.isPDF(params.output) && isBlob(response.data)) {
      const today = dayjs();
      const filename = `Game Definition - ${today.format("MM-DD-YYYY")}`;

      downloadBlob(response.data, {
        name: filename,
        behavior: DownloadBlobConfigBehavior.DIRECT_DOWNLOAD,
      });

      return DEFAULT_RESPONSE;
    }

    if (Output.isHTML(params.output) && isString(response.data)) {
      return response.data;
    }

    return DEFAULT_RESPONSE;
  } catch (error) {
    notificationError("Could not get report");
    return DEFAULT_RESPONSE;
  }
}

export type GetLocationInventoryParams = {
  name: string;
  output: Output;
  fullMonth: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
};

export async function getLocationInventory(params: GetLocationInventoryParams) {
  return downloadOrRender<GetLocationInventoryParams>(
    "Location Inventory",
    "get",
    endpoints.locationinventory,
    params
  );
}

export type GetMonthEndParams = {
  output: Output;
  fullMonth: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
};

export async function getMonthEnd(params: GetMonthEndParams) {
  return downloadOrRender<GetMonthEndParams>(
    "Month End",
    "get",
    endpoints.monthend,
    params
  );
}

export type GameDefinitionFilter = {
  value: string;
  text: string;
};

export type GetGameDefinitionFilterResponse = GameDefinitionFilter[];

export async function getGameDefinitionFilter(): Promise<GetGameDefinitionFilterResponse> {
  const response = await Api.get<
    any,
    AxiosResponse<GetGameDefinitionFilterResponse>
  >(endpoints.gamedefinition_filter);

  return response.data;
}

export type CharitiesProgressiveContributionFilter = {
  value: string;
  text: string;
};

export type CharitiesProgressiveContributionFilterResponse =
  CharitiesProgressiveContributionFilter[];

export async function getCharitiesProgressiveContributionFilter() {
  const response = await Api.get<
    any,
    AxiosResponse<CharitiesProgressiveContributionFilterResponse>
  >(endpoints.charitiesProgressiveContribution_filter);

  return response.data;
}

export type GetCashierActivityParams = {
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  beginDate: string;
  endDate: string;
};

export type GetCashierActivityResponse =
  | {
      error: boolean;
      message: string;
      data: string;
    }
  | string
  | Blob;

export async function getCashierActivity(params: GetCashierActivityParams) {
  const DEFAULT_RESPONSE = "";

  try {
    const response = await Api.get<
      any,
      AxiosResponse<GetCashierActivityResponse>
    >(endpoints.cashieractivity, {
      params,
      responseType: responseTypes[params.output],
    });

    if (response === null) {
      return DEFAULT_RESPONSE;
    }

    if (
      typeof response.data === "object" &&
      "error" in response.data &&
      "message" in response.data
    ) {
      notificationError(`${response.data.message}`);
      return DEFAULT_RESPONSE;
    }

    if (Output.isPDF(params.output) && isBlob(response.data)) {
      const today = dayjs();
      const filename = `Cashier Activity - ${today.format("MM-DD-YYYY")}`;

      downloadBlob(response.data, {
        name: filename,
        behavior: DownloadBlobConfigBehavior.DIRECT_DOWNLOAD,
      });

      return DEFAULT_RESPONSE;
    }

    if (Output.isHTML(params.output) && isString(response.data)) {
      return response.data;
    }

    return DEFAULT_RESPONSE;
  } catch (error) {
    notificationError("Could not get report");
    return DEFAULT_RESPONSE;
  }
}

export type GetDailyDepositParams = {
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  beginDate: string;
  endDate: string;
};

export async function getDailyDeposit(params: GetDailyDepositParams) {
  return downloadOrRender<GetDailyDepositParams>(
    "Daily Deposit",
    "get",
    endpoints.dailyDeposit,
    params
  );
}

export type GetDailyDepositSalesParams = {
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  beginDate: string;
  endDate: string;
};

export async function getDailyDepositSales(params: GetDailyDepositSalesParams) {
  return downloadOrRender<GetDailyDepositSalesParams>(
    "Daily Deposit - Sales",
    "get",
    endpoints.dailyDepositSales,
    params
  );
}

export type GetDailySalesDetailParams = {
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  beginDate: string;
  endDate: string;
};

export async function getDailySalesDetail(params: GetDailySalesDetailParams) {
  return downloadOrRender<GetDailySalesDetailParams>(
    "Daily Sales Detail",
    "get",
    endpoints.dailySalesDetail,
    params
  );
}

export type GetPeriodRevenueParams = {
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  beginDate: string;
  endDate: string;
};

export async function getPeriodRevenue(params: GetPeriodRevenueParams) {
  return downloadOrRender<GetPeriodRevenueParams>(
    "Period Revenue",
    "get",
    endpoints.periodRevenue,
    params
  );
}

export type GetLargeCashOutParams = {
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  beginDate: string;
  endDate: string;
};

export async function getLargeCashOut(params: GetLargeCashOutParams) {
  return downloadOrRender<GetLargeCashOutParams>(
    "Large Cashout Activity",
    "get",
    endpoints.largeCashOut,
    params
  );
}

export type GetLocationLG861eParams = {
  output: Output;
  locationId: string;
  month: string;
  year: string;
};

export type GetLocationLG861eResponse =
  | {
      error: boolean;
      message: string;
      data: string;
    }
  | string
  | Blob;

export async function getLocationLG861e(params: GetLocationLG861eParams) {
  const DEFAULT_RESPONSE = "";

  try {
    const response = await Api.get<
      any,
      AxiosResponse<GetLocationLG861eResponse>
    >(endpoints.locationLG861e, {
      params,
      responseType: responseTypes[params.output],
    });

    if (response === null) {
      return DEFAULT_RESPONSE;
    }

    if (
      typeof response.data === "object" &&
      "error" in response.data &&
      "message" in response.data
    ) {
      notificationError(`${response.data.message}`);
      return DEFAULT_RESPONSE;
    }

    if (Output.isPDF(params.output) && isBlob(response.data)) {
      const today = dayjs();
      const filename = `Location LG861e - ${today.format("MM-DD-YYYY")}`;

      downloadBlob(response.data, {
        name: filename,
        behavior: DownloadBlobConfigBehavior.DIRECT_DOWNLOAD,
      });

      return DEFAULT_RESPONSE;
    }

    if (Output.isHTML(params.output) && isString(response.data)) {
      return response.data;
    }

    return DEFAULT_RESPONSE;
  } catch (error) {
    notificationError("Could not get report");
    return DEFAULT_RESPONSE;
  }
}

export type GetGamePerformanceReportParams = {
  name: string;
  beginDate: string;
  endDate: string;
  output: Output;
};

export function getGamePerformanceReport(
  params: GetGamePerformanceReportParams
) {
  return downloadOrRender<GetGamePerformanceReportParams>(
    "Game performance",
    "get",
    endpoints.getGamePerformanceReport,
    params
  );
}

export type GetGamePerformanceWithVariantsReportParams = {
  beginDate: string;
  endDate: string;
  output: Output;
};

export function getGamePerformanceWithVariants(
  params: GetGamePerformanceWithVariantsReportParams
) {
  return downloadOrRender<GetGamePerformanceWithVariantsReportParams>(
    "Game performance with variants",
    "get",
    endpoints.getGamePerformanceWithVariants,
    params
  );
}

export type GetDrawerActivityReportParams = {
  date: string;
  locationId?: string;
  output: Output;
};

export function getDrawerActivityReport(params: GetDrawerActivityReportParams) {
  return downloadOrRender<GetDrawerActivityReportParams>(
    "Drawer Activity",
    "get",
    endpoints.getDrawerActivityReport,
    params
  );
}

export type GetBingoAccountParams = {
  output: Output;
  distributorId?: string;
  charityId?: string;
  month: string;
};

export function getBingoAccount(params: GetBingoAccountParams) {
  return downloadOrRender<GetBingoAccountParams>(
    "Bingo Account",
    "get",
    endpoints.getBingoAccount,
    params
  );
}

export type MonthlyReportsGameTypeFilter = {
  [name: string] : string;
};

export type MonthlyReportsGameTypeFilterResponse =
MonthlyReportsGameTypeFilter[];

export async function getMonthlyReportsGameTypeFilter() {
  const response = await Api.get<
    any,
    AxiosResponse<MonthlyReportsGameTypeFilterResponse>
  >(endpoints.getMonthlyReportsGameTypeFilter);

  return response.data;
}

export type GetMonthlyReportsParams = {
  output: Output;
  locationId: string;
  gametype: string;
  month: string | number;
  year: string;
};

export type GetMonthlyReportsResponse = {
  // Pendente: Atualmente o end-point encontra-se indisponível, não sabemos como é o retorno.
};

export function getMonthlyReports(params: GetMonthlyReportsParams) {
  return downloadOrRender<GetMonthlyReportsParams>(
    "Monthly Reports",
    "get",
    endpoints.getMonthlyReports,
    params
  );
}

export type GetBingoCharityParams = {
  output: Output;
  distributorId?: string;
  charityId?: string;
  fullMonth: string;
};

export function getBingoCharityInvoice(params: GetBingoCharityParams) {
  return downloadOrRender<GetBingoCharityParams>(
    "Bingo Charity Monthly Invoice",
    "get",
    endpoints.getBingoCharityInvoice,
    params
  );
}

export type GetRevenueByDeviceParams = {
  beginDate: string;
  endDate: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  output: Output;
  day_week_month_year_select: string;
};

export function getRevenueByDevice(params: GetRevenueByDeviceParams) {
  return downloadOrRender<GetRevenueByDeviceParams>(
    "Revenue by device",
    "get",
    endpoints.getRevenueByDevice,
    params
  );
}

export type GetBingoDistributorParams = {
  output: Output;
  distributorId?: string;
  charityId?: string;
  month: string;
};

export function getBingoDistributor(params: GetBingoDistributorParams) {
  return downloadOrRender<GetBingoDistributorParams>(
    "Bingo Distributor Monthly Statement",
    "get",
    endpoints.getBingoDistributor,
    params
  );
}

export type GetBingoGameLogParams = {
  output: Output;
  beginDateTime: string;
  endDateTime: string;
  searchExpression: string;
};

export function getBingoGameLog(params: GetBingoGameLogParams) {
  return downloadOrRender<GetBingoGameLogParams>(
    "Bingo Game Log",
    "get",
    endpoints.getBingoGameLog,
    params
  );
}

export type GetCharitiesProgressiveContribution = {
  jackpotHistorySelect: string;
  output: Output;
};

export function getCharitiesProgressiveContribution(
  params: GetCharitiesProgressiveContribution
) {
  return downloadOrRender<GetCharitiesProgressiveContribution>(
    "Bingo Charities Progressive Contribution",
    "get",
    endpoints.getCharitiesProgressiveContribution,
    params
  );
}

export type GetRevenueByGameParams = {
  beginDate: string;
  endDate: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  output: Output;
};

export function getRevenueByGame(params: GetRevenueByGameParams) {
  return downloadOrRender<GetRevenueByGameParams>(
    "Revenue by game",
    "get",
    endpoints.getRevenueByGame,
    params
  );
}

export type GetRevenueByPeriodParams = {
  beginDate: string;
  endDate: string;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  output: Output;
  day_week_month_year_select: string;
};

export function getRevenueByPeriod(params: GetRevenueByPeriodParams) {
  return downloadOrRender<GetRevenueByPeriodParams>(
    "Revenue by period",
    "get",
    endpoints.getRevenueByPeriod,
    params
  );
}

export type GetBingoLBPMonthlyParams = {
  output: Output;
  month: string;
};

export function getBingoLBPMonthly(params: GetBingoLBPMonthlyParams) {
  return downloadOrRender<GetBingoLBPMonthlyParams>(
    "Bingo LBP Monthly",
    "get",
    endpoints.getBingoLBPMonthly,
    params
  );
}

export type ProgressiveContribution = {
  value: string;
  text: string;
};

export type GetProgressiveContributionsResponse = ProgressiveContribution[];

export async function getProgressiveContributions() {
  try {
    const response = await Api.get<
      any,
      AxiosResponse<GetProgressiveContributionsResponse>
    >(endpoints.getProgressiveContributions);

    return response.data;
  } catch (e) {
    if (e instanceof Error) {
      notificationError(e.message);
    }

    notificationError("Cannot get progressive contributions");
  }
}

export type GetBingoProgressiveContributionParams = {
  locationId?: string;
  distributorId?: string;
  charityId?: string;
  beginDate: string;
  endDate: string;
  groupByCompanySelect: string;
  jackpotTemplateSelect: string;
};

export async function getBingoProgressiveContribution(
  params: GetBingoProgressiveContributionParams
) {
  const DEFAULT_RESPONSE = "";

  try {
    const response = await Api.get<
      any,
      AxiosResponse<GetLocationLG861eResponse>
    >(endpoints.getBingoProgressiveContribution, {
      params,
      responseType: responseTypes.pdf,
    });

    if (response === null) {
      return DEFAULT_RESPONSE;
    }

    if (isBlob(response.data)) {
      const today = dayjs();
      const filename = `Bingo Progressive Contribution - ${today.format(
        "MM-DD-YYYY"
      )}`;

      downloadBlob(response.data, {
        name: filename,
        behavior: DownloadBlobConfigBehavior.DIRECT_DOWNLOAD,
      });

      return DEFAULT_RESPONSE;
    }

    return DEFAULT_RESPONSE;
  } catch (error) {
    notificationError("Could not get report");
    return DEFAULT_RESPONSE;
  }
}

export type GetBingoJackpotWinnersParams = {
  output: Output;
};

export function getBingoJackpotWinners(params: GetBingoJackpotWinnersParams) {
  return downloadOrRender<GetBingoJackpotWinnersParams>(
    "Bingo Jackpot Winners",
    "get",
    endpoints.getBingoJackpotWinners,
    params
  );
}

export type GetBingoActiveSitesParams = {
  output: Output;
  locationId?: string;
  distributorId?: string;
  charityId?: string;
};

export function getBingoActiveSites(params: GetBingoActiveSitesParams) {
  return downloadOrRender<GetBingoActiveSitesParams>(
    "Bingo Active Sites",
    "get",
    endpoints.getBingoActiveSites,
    params
  );
}

export type GetBingoActiveProgressivesParams = {
  output: Output;
};

export function getBingoActiveProgressives(
  params: GetBingoActiveProgressivesParams
) {
  return downloadOrRender<GetBingoActiveProgressivesParams>(
    "Bingo Active Progressives",
    "get",
    endpoints.getBingoActiveProgressives,
    params
  );
}

export type GetBingoPlayReportParams = {
  output: Output;
  beginDate: string;
  endDate: string;
};

export function getBingoPlayReport(params: GetBingoPlayReportParams) {
  return downloadOrRender<GetBingoPlayReportParams>(
    "Bingo play",
    "get",
    endpoints.getBingoPlayReport,
    params
  );
}
export type GetBingoPerformanceReportParams = {
  output: Output;
  dateIntervalStart: string;
  dateIntervalEnd: string;
};

export function getBingoPerformanceReport(
  params: GetBingoPerformanceReportParams
) {
  return downloadOrRender<GetBingoPerformanceReportParams>(
    "Bingo performance",
    "get",
    endpoints.getBingoPerformanceReport,
    params
  );
}

export type GetUserListReportParams = {
  output: Output;
  userSearch?: string;
};

export function getUserListReport(
  params: GetUserListReportParams
) {
  return downloadOrRender<GetUserListReportParams>(
    "User List",
    "get",
    endpoints.getUserListReport,
    params
  );
}

export async function getStorageUsageVersions() {
  var res;
  await Api.get(endpoints.getStorageUsageVersions, {  })
      .then((response) => {
        if (response.data.error) {
          notificationError(`${response.data.message}`);
        } else {
          res = response.data;
        }
      })
      .catch((err: string) => {
        notificationError("Could not get report");
      });

  return res;
}
